import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, getErrMsg } from "../lib/api";
import { formatTL } from "../lib/format";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Skeleton } from "./ui/skeleton";
import { FileSpreadsheet, Loader2, Save, Trash2 } from "lucide-react";

const AYLAR = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];

const SATIRLAR = [
  { key: "net_satislar", label: "Net Satışlar" },
  { key: "satislarin_maliyeti", label: "Satışların Maliyeti (SMM)" },
  { key: "faaliyet_giderleri", label: "Faaliyet Giderleri" },
  { key: "diger_gelirler", label: "Diğer Gelirler" },
  { key: "diger_giderler", label: "Diğer Giderler" },
  { key: "finansman_giderleri", label: "Finansman Giderleri" },
  { key: "vergi", label: "Vergi" },
];
const BOS = Object.fromEntries(SATIRLAR.map((s) => [s.key, ""]));

export const GelirTablosuSekmesi = ({ yonetici, yil = 2026 }) => {
  const [kayitlar, setKayitlar] = useState(null);
  const [ay, setAy] = useState("8");
  const [form, setForm] = useState(BOS);
  const [notlar, setNotlar] = useState("");
  const [saving, setSaving] = useState(false);
  const kirliRef = useRef(false);

  const fetchKayitlar = useCallback(async () => {
    try {
      const res = await api.get("/gelir-tablosu", { params: { yil } });
      setKayitlar(res.data);
    } catch (err) {
      toast.error(getErrMsg(err, "Gelir tablosu yüklenemedi."));
    }
  }, [yil]);

  useEffect(() => {
    fetchKayitlar();
  }, [fetchKayitlar]);

  // Ay değişince mevcut kayıt formu doldursun (kullanıcı yazmaya başladıysa geç gelen yanıt formu ezmez)
  useEffect(() => {
    if (kirliRef.current) return;
    const mevcut = kayitlar?.find((k) => k.ay === parseInt(ay));
    if (mevcut) {
      setForm(Object.fromEntries(SATIRLAR.map((s) => [s.key, String(mevcut[s.key] ?? "")])));
      setNotlar(mevcut.notlar || "");
    } else {
      setForm(BOS);
      setNotlar("");
    }
  }, [ay, kayitlar]);

  const ayDegistir = (v) => {
    kirliRef.current = false;
    setAy(v);
  };

  const alanYaz = (key, v) => {
    kirliRef.current = true;
    setForm((f) => ({ ...f, [key]: v }));
  };

  const num = (v) => parseFloat(v) || 0;
  const brutKar = num(form.net_satislar) - num(form.satislarin_maliyeti);
  const faaliyetKari = brutKar - num(form.faaliyet_giderleri);
  const netKar = faaliyetKari + num(form.diger_gelirler) - num(form.diger_giderler) - num(form.finansman_giderleri) - num(form.vergi);

  const kaydet = async () => {
    setSaving(true);
    try {
      const res = await api.post("/gelir-tablosu", {
        yil,
        ay: parseInt(ay),
        ...Object.fromEntries(SATIRLAR.map((s) => [s.key, num(form[s.key])])),
        notlar,
      });
      toast.success(res.data.guncellendi ? "Gelir tablosu güncellendi." : "Gelir tablosu kaydedildi.");
      kirliRef.current = false;
      fetchKayitlar();
    } catch (err) {
      toast.error(getErrMsg(err, "Gelir tablosu kaydedilemedi."));
    } finally {
      setSaving(false);
    }
  };

  const sil = async (id) => {
    try {
      await api.delete(`/gelir-tablosu/${id}`);
      toast.success("Gelir tablosu kaydı silindi.");
      fetchKayitlar();
    } catch (err) {
      toast.error(getErrMsg(err, "Silinemedi."));
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="gelir-tablosu-sekmesi">
      {/* Giriş formu */}
      <div className="rounded-xl border bg-card p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-display text-sm font-semibold flex items-center gap-2">
            <FileSpreadsheet className="h-4 w-4" /> Aylık Gelir Tablosu Girişi
          </h3>
          <Select value={ay} onValueChange={ayDegistir}>
            <SelectTrigger className="h-10 w-36" data-testid="gelir-tablosu-ay-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card">
              {AYLAR.map((a, i) => (
                <SelectItem key={a} value={String(i + 1)}>{a} {yil}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <p className="text-[11px] text-muted-foreground">Ağustos {yil} itibarıyla manuel takip. Ara toplamlar otomatik hesaplanır.</p>
        <div className="space-y-2">
          {SATIRLAR.map((s) => (
            <div key={s.key} className="flex items-center justify-between gap-3">
              <Label className="text-xs">{s.label}</Label>
              <Input
                type="number" inputMode="decimal" step="0.01" placeholder="0,00"
                value={form[s.key]} onChange={(e) => alanYaz(s.key, e.target.value)}
                disabled={!yonetici}
                className="h-10 w-44 text-right tabular-nums" data-testid={`gelir-tablosu-${s.key}-input`}
              />
            </div>
          ))}
          <Input
            placeholder="Not (opsiyonel)" value={notlar} onChange={(e) => { kirliRef.current = true; setNotlar(e.target.value); }}
            disabled={!yonetici} className="h-10" data-testid="gelir-tablosu-not-input"
          />
        </div>
        <div className="rounded-lg border bg-muted/30 p-3 space-y-1 text-sm tabular-nums" data-testid="gelir-tablosu-ara-toplamlar">
          <p className="flex justify-between"><span className="text-muted-foreground">Brüt Kâr</span><strong className={brutKar < 0 ? "text-red-700" : "text-emerald-700"}>{formatTL(brutKar)}</strong></p>
          <p className="flex justify-between"><span className="text-muted-foreground">Faaliyet Kârı</span><strong className={faaliyetKari < 0 ? "text-red-700" : "text-emerald-700"}>{formatTL(faaliyetKari)}</strong></p>
          <p className="flex justify-between border-t border-border/60 pt-1"><span className="font-semibold">Net Dönem Kârı</span><strong className={netKar < 0 ? "text-red-700" : "text-emerald-700"} data-testid="gelir-tablosu-net-kar">{formatTL(netKar)}</strong></p>
        </div>
        {yonetici && (
          <Button onClick={kaydet} disabled={saving} className="min-h-[44px] w-full gap-1.5" data-testid="gelir-tablosu-kaydet-button">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Kaydet
          </Button>
        )}
      </div>

      {/* Girilen aylar */}
      <div className="rounded-xl border bg-card p-4 space-y-3">
        <h3 className="font-display text-sm font-semibold">Girilen Dönemler ({yil})</h3>
        {!kayitlar ? (
          <Skeleton className="h-40" />
        ) : kayitlar.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center" data-testid="gelir-tablosu-bos">
            Henüz gelir tablosu girilmedi. Soldaki formdan Ağustos {yil} ile başlayabilirsiniz.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[460px] text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-2 py-2">Dönem</th>
                  <th className="px-2 py-2 text-right">Net Satışlar</th>
                  <th className="px-2 py-2 text-right">Brüt Kâr</th>
                  <th className="px-2 py-2 text-right">Net Kâr</th>
                  {yonetici && <th className="px-2 py-2"></th>}
                </tr>
              </thead>
              <tbody data-testid="gelir-tablosu-liste">
                {kayitlar.map((k) => (
                  <tr
                    key={k.id}
                    className={`border-t border-border/50 cursor-pointer hover:bg-muted/30 ${parseInt(ay) === k.ay ? "bg-muted/40" : ""}`}
                    onClick={() => ayDegistir(String(k.ay))}
                    data-testid={`gelir-tablosu-satir-${k.ay}`}
                  >
                    <td className="px-2 py-2 font-medium">{AYLAR[k.ay - 1]} {k.yil}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{formatTL(k.net_satislar)}</td>
                    <td className={`px-2 py-2 text-right tabular-nums ${k.brut_kar < 0 ? "text-red-700" : ""}`}>{formatTL(k.brut_kar)}</td>
                    <td className={`px-2 py-2 text-right tabular-nums font-semibold ${k.net_donem_kari < 0 ? "text-red-700" : "text-emerald-700"}`}>{formatTL(k.net_donem_kari)}</td>
                    {yonetici && (
                      <td className="px-2 py-2 text-right">
                        <Button
                          variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={(e) => { e.stopPropagation(); sil(k.id); }}
                          data-testid={`gelir-tablosu-sil-${k.ay}`} aria-label="Kaydı sil"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
